/**
 * Populates the info-tooltip (util/tooltip/info-tooltip.ts) nested source
 * text for every IP-geolocation link in a freshly-rendered container
 * (ISS-09-009). Reuses that existing hover/positioning/mobile-popup engine
 * instead of building a dedicated popover — the IP link just needs to be a
 * valid info-tooltip trigger with its nested source text kept up to date.
 */

import { Roles } from "../../constants/roles";
import { translator } from "../../translation/translator";
import { FormBase } from "../../util/form-base-form";
import { IpGeolocationApi, type IpGeolocationResult } from "./ip-geolocation-api";

class IpGeolocationTooltipForm extends FormBase {
    public getSources(container: HTMLElement): HTMLElement[] {
        return this.queryElements(container, `[data-role="${Roles.INFO_TOOLTIP_SOURCE}"][data-ip]`, HTMLElement);
    }
}

const ipGeolocationTooltipForm = new IpGeolocationTooltipForm();

function formatGeolocationText(result: IpGeolocationResult | null): string {
    if (!result) {
        return translator.translate("ip_geolocation_unavailable");
    }

    const lines = [translator.translate("ip_geolocation_title")];
    if (result.country) lines.push(`${translator.translate("ip_geolocation_country")}: ${result.country}`);
    if (result.region) lines.push(`${translator.translate("ip_geolocation_region")}: ${result.region}`);
    if (result.city) lines.push(`${translator.translate("ip_geolocation_city")}: ${result.city}`);
    if (result.org) lines.push(`${translator.translate("ip_geolocation_org")}: ${result.org}`);

    return lines.length > 1 ? lines.join("\n") : translator.translate("ip_geolocation_unavailable");
}

export function populateIpGeolocationTooltips(container: HTMLElement): void {
    ipGeolocationTooltipForm.getSources(container).forEach((source) => {
        const ip = source.dataset.ip?.trim() ?? "";
        if (!ip) return;

        void IpGeolocationApi.fetch(ip).then((result) => {
            source.textContent = formatGeolocationText(result);
        });
    });
}
